import { Popover, Transition } from "@headlessui/react";
import { FloatingItem, Header, Intro } from "@prmf/site-components";
import { routes, t } from "@prmf/site-router";
import { useActionData } from "@remix-run/react";
import { Fragment, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";

type ItemProps = Omit<React.ComponentProps<typeof FloatingItem>, "children"> & {
  text: string;
  videoId: string;
  linkLabel?: string;
};

const Item: React.FC<ItemProps> = (props) => {
  return (
    <FloatingItem title={props.title} image={props.image}>
      <p>{props.text}</p>
      <Popover className="relative">
        <Popover.Button className="underline">
          {props.linkLabel || "Find out more"}
        </Popover.Button>
        <Transition
          as={Fragment}
          enter="transition ease-in"
          leave="transition ease-out"
          enterFrom="opacity-0"
          leaveTo="opacity-0"
        >
          <Popover.Panel className="fixed inset-0 z-50 grid place-items-center">
            <Popover.Overlay className="absolute inset-0 bg-black/70" />
            <Transition.Child
              as={Fragment}
              enter="transition ease-in"
              leave="transition ease-out"
              enterFrom="translate-y-20"
              leaveTo="translate-y-20"
            >
              <iframe
                src={`https://player.vimeo.com/video/${props.videoId}?autoplay=1&title=0&byline=0&portrait=0`}
                className={twMerge(
                  "relative",
                  "max-w-[90%] max-lg:w-full",
                  "max-h-[500px] lg:h-full",
                  "aspect-video",
                  "rounded-lg bg-black",
                )}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </Transition.Child>
          </Popover.Panel>
        </Transition>
      </Popover>
    </FloatingItem>
  );
};

export default function SustainabilityBook() {
  const formRef = useRef<HTMLFormElement>(null);
  const data = useActionData<{ ok?: true }>();

  useEffect(() => {
    if (data?.ok) {
      toast.success(
        "Thank you for your interest. Your message has been sent and we will be in touch soon.",
        { id: "contact" },
      );
      formRef.current?.reset();
    }
  }, [data, formRef]);

  return (
    <div>
      <Header
        title={t(routes.sustainabilityBook, "title")}
        image={t(routes.sustainabilityBook, "image")}
      >
        <p>
          It’s essential for our clients that our products are not only of the
          highest quality and built to last, but also that those factors are not
          achieved at a cost to the environment. They too have CSR targets to
          meet and it became increasingly important for us to communicate our
          environmental credentials, showing us to be a forward-thinking and
          sustainable partner.
        </p>
      </Header>
      <Intro title="Find out more">
        <p>
          From timber grown in FSC&reg;-certified forests to zero carbon
          emissions in our manufacturing processes to decreasing our energy
          consumption, Paula Rosa Manhattan is committed to reducing waste
          further whilst encouraging others to do the same. The Little Book of
          Sustainability is printed on paper from FSC&reg;-certified and
          sustainable forests and the production processes were carbon balanced.
          It is 100% recyclable although far too lovely to be thrown away.
        </p>
      </Intro>
      <ol className="container my-20 grid gap-20 pt-20 lg:grid-cols-2">
        <Item
          title="Trees"
          text="Let’s face it, we use trees to make our products. But we are also aware of our responsibility to the planet."
          image="/media/assets/book-trees.jpg"
          videoId="437957773"
        />
        <Item
          title="Emissions"
          text="Our operations have unwanted CO2 emissions but we have identified these and have actions in place to reduce them."
          image="/media/assets/book-emissions.jpg"
          videoId="443018092"
          linkLabel="See these actions detail"
        />
        <Item
          title="Energy Consumption"
          image="/media/assets/book-lighting.jpg"
          text="Although 100% of our energy is already from renewable sources, we can still take steps to reduce our consumption."
          videoId="443091122"
          linkLabel="See what steps we are taking"
        />
        <Item
          title="Waste"
          image="/media/assets/book-waste.jpg"
          text="In manufacturing and distribution processes some of the planet’s resources are wasted."
          videoId="444075003"
          linkLabel="See how we are mitigating this"
        />
        <Item
          title="Circular Economy"
          image="/media/assets/book-recycle.jpg"
          text="We recognise that the kitchen industry is subject to changing trends and tastes. We want to make it easier for homeowners to re-use and recycle our products."
          videoId="444071056"
          linkLabel="See how we do this"
        />
        <Item
          title="The Local Community"
          image="/media/assets/book-community.jpg"
          text="We want to have a lasting positive impact on the local community through both employment and by working with local organisations related to homes and the environment"
          videoId="443346815"
          linkLabel="See what we are doing"
        />
        <Item
          title="Our people"
          image="/media/assets/book-safety.jpg"
          text="We are committed to creating an environment where every employee can work at their best."
          videoId="444079689"
          linkLabel="See what actions we take as a business"
        />
        <Item
          title="Future Challenges"
          image="/media/assets/book-future.jpg"
          text="There will always be room for improvement and reflection."
          videoId="442700268"
          linkLabel="See in detail our next challenges"
        />
      </ol>
    </div>
  );
}
